import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, Signal } from '@angular/core';
import { OffsetTimePipe } from '@lx/quote-tool/shared/ui/pipes';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { StudyPeriodPipe } from '@lx/quote-tool/lite/shared/ui/pipes';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteOutputCourse, QuoteStore, QuoteToolLiteStore } from '../../store';
import { CoursePayload } from '../../types';
import { LanguageCampsComponent } from '../language-camps/language-camps.component';
import { LanguageSchoolsComponent } from '../language-schools/language-schools.component';
import { OnlineCoursesComponent } from '../online-courses/online-courses.component';
import { CardContainerComponent, SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { YourCourseData } from './your-course-data.type';
import { OperationKey } from '../../constants';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  selector: 'lx-your-course',
  templateUrl: './your-course.component.html',
  styleUrls: ['./your-course.component.scss'],
  imports: [
    CommonModule,
    SidePanelComponent,
    StudyPeriodPipe,
    LanguageSchoolsComponent,
    CardContainerComponent,
    LanguageCampsComponent,
    OnlineCoursesComponent,
    OffsetTimePipe,
  ],
})
export class YourCourseComponent implements OnInit {
  dialogVisible = false;
  validCoursePayload = false;
  coursePayload: CoursePayload | undefined;
  yourCourseData$: Observable<YourCourseData & QuoteOutputCourse> | undefined;
  activeFlow$: Observable<Flow> | undefined;
  currency: Signal<string | undefined>;
  dataLayerEventName = GoogleAnalyticsEventEnum.QuoteCourseEdit;

  protected loading: Signal<boolean | undefined>;

  protected readonly Flows = Flows;

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly loadingService = inject(LoadingService);

  ngOnInit(): void {
    this.activeFlow$ = this.quoteToolLiteStore.activeFlow$;

    this.yourCourseData$ = combineLatest([this.quoteToolLiteStore.yourCourseData$, this.quoteStore.quoteCourse$]).pipe(
      map(([{ selectedSchoolLocation, selectedTimezone, selectedClassTime }, selectedCourse]) => ({
        ...selectedSchoolLocation,
        ...selectedCourse,
        selectedClassTime,
        selectedTimezone,
      })),
    );

    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.extras, OperationKey.prices]);
  }

  openDialog() {
    this.dialogVisible = true;
  }

  hideDialog() {
    this.dialogVisible = false;
  }

  /**
   * Updates the validity of the course payload
   */
  updateValidityCoursePayload(validCoursePayload: boolean) {
    this.validCoursePayload = validCoursePayload;
  }

  /**
   * Saves the course selection to Quote Tool Lite store and closes the dialog
   */
  saveCourseSelection() {
    if (this.coursePayload) {
      this.quoteToolLiteStore.updateState(this.coursePayload);
    }
    this.hideDialog();
  }

  /**
   * Updates temporary course payload
   */
  updateCoursePayload(coursePayload: CoursePayload) {
    this.coursePayload = coursePayload;
  }
}
